<template>
  <div class="col-12 grid">
    <VueConfirmDialog :parent="_uid" />
    <div class="row">
      <div class="col-12 head images title">
        IMAGES
      </div>
      <div :class="{ images: images && images.length > 0 }" class="col-12">
        <transition-group
          name="change-image"
          enter-active-class="animation fadeIn"
          leave-active-class="animation fadeOut"
          class="row"
          tag="div"
        >
          <div
            class="col-12 col-md-6"
            v-for="(item, index) in images"
            :key="item.thumb"
          >
            <img
              class="pointer thumbs"
              @click="openGallery(index)"
              :src="item.thumb"
            />
            <div id="image-tools">
              <span
                class="material-symbols-outlined preview pointer"
                @click="openGallery(index)"
                >open_in_full</span
              >
              <span
                v-if="!readOnly"
                @click="deleteImage(index)"
                class="material-symbols-outlined delete pointer"
                >delete</span
              >
            </div>
          </div>
        </transition-group>

        <div v-if="!readOnly" class="container">
          <div class="row">
            <div
              class="col-12 head images"
              :class="{ images: images && images.length > 0 }"
            >
              <div class="push-down-20 images">
                <input
                  v-show="currentRuxImages.length < 6"
                  ref="uploader"
                  type="file"
                  @change="onFileSelected"
                  id="files"
                  name="files[]"
                  class="image-uploader"
                  accept="image/*"
                  placeholder="For testing only"
                />
                <button
                  v-if="showUploadButton"
                  class="button push-down-20 pointer upload-button"
                  @click="prepareImageForUpload"
                >
                  Upload
                </button>
                <div
                  data-testid="tooManyImages"
                  v-if="currentRuxImages.length >= 6"
                >
                  You have reached the maximum number of images for this RUX.
                  Please delete some to add more.
                </div>
                <loading
                  color="rgb(187, 57, 54)"
                  :active.sync="showUploadProgess"
                  :is-full-page="false"
                  loader="bars"
                ></loading>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Lightbox
      :disableScroll="false"
      v-if="(images.length > 0)"
      :showLightBox="false"
      :media="images"
      ref="lightbox"
      data-testid="lightbox"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import Lightbox from 'vue-image-lightbox'
import 'vue-loading-overlay/dist/vue-loading.css'
import 'vue-image-lightbox/dist/vue-image-lightbox.min.css'
import VueConfirmDialog from './ConfirmDialog/vue-confirm-dialog.vue'

// Handles displaying the images and uploading them.
// To display the images, it makes a call to the store with the serialnumber then watch a property in the store.
export default {
  name: 'RuxDetailsImages',
  components: { Loading, Lightbox, VueConfirmDialog },
  props: ['currentRuxImages', 'readOnly'],
  data: function () {
    return {
      // V-model for the upload field
      selectedFile: null,
      showUploadButton: false,
      showUploadProgess: false,
    }
  },
  computed: {
    // The currentRuxImages property in the state. This method getImages below gets the images from the db
    // and assigns them to this property
    // ...mapState(['currentRuxImages']),
    images: function () {
      return (
        // The image lightbox expects both a src and thumb url so thats all that this does.
        this.currentRuxImages.map((image) => {
          return {
            src: image,
            thumb: image,
          }
        }) || {}
      )
    },
  },
  methods: {
    onFileSelected(e) {
      this.selectedFile = e.target.files[0]
      this.showUploadButton = true
    },
    /**
     * This happens after the user has confirmed whether they want to upload the image.
     * @param {Boolean} replace - whether or not to replace/overwrite the image
     */
    async uploadImage(replace) {
      // Do some UI stuff to show the user something is happening.
      this.showUploadButton = false
      this.showUploadProgess = true
      // Send the image to the store. This takes care of uploading it and updating the UI
      this.$emit('addImage', {
        imageData: {
          image: this.selectedFile,
          serialNumber: this.$route.params.serialNumber,
          replace,
        },
        callback: () => {
          // Reset the form here
          this.$refs.uploader.value = null
          this.showUploadProgess = false
        },
      })
    },
    prepareImageForUpload() {
      // This checks to see whether the image already exists in the array of current images.
      // Loops through all the images in the current array here
      const imageWithSameName = this.images.find((image) => {
        // Find the safe file name of the selected file. This strips out any characters that firebase doesnt like
        const safeFileName = this.selectedFile.name.replace(
          // Don't like having this in 2 spots
          /[@ ()*;<>{}/[\]]/g,
          ''
        )
        // image.src is the URL of the image, not just the name. This function extracts just the filename out.
        const newImageName = this.findImageName(image.src)
        // return true if they match
        return safeFileName === newImageName
      })
      // If the file already exists the user needs to be prompted to cancel/overwrite/keep both
      // This uses the confirm dialog to help with that
      if (imageWithSameName) {
        this.$confirm({
          parent: this._uid,
          message:
            'An image with the same name already exists! Please select an option',
          buttons: [
            {
              // Do nothing on cancel
              text: 'Cancel',
            },
            {
              text: 'Keep Both',
              callback: () => {
                // Handle keeping both by adding a unixtime stamp to the front.
                const newFile = new File(
                  [this.selectedFile],
                  Date.now() + '-' + this.selectedFile.name
                )
                // Upload the image here
                this.selectedFile = newFile
                this.uploadImage()
              },
            },
            {
              // Handle replacing by passing in a true value for replace
              text: 'Replace!',
              callback: () => {
                this.uploadImage(true)
              },
            },
          ],
          onCancelCallback: () => {},
        })
      } else {
        // This fires when the image is uniquely named
        this.uploadImage()
      }
    },
    openGallery(index) {
      this.$refs.lightbox.showImage(index)
    },
    // Finds an image name from a URL
    findImageName(imagePath) {
      //TODO Figure out a way to get the file name that doesn't necessarily rely on ?alt=token as that can change from Firebase
      return imagePath.match('%2..*%2F(.*?)?alt')[1].slice(0, -1)
    },
    // Deletes the image
    deleteImage(index) {
      this.$confirm({
        parent: this._uid,
        message: 'Really delete this image? This cannot be undone',
        buttons: [
          {
            text: 'Cancel',
          },
          {
            text: 'Delete',
            callback: () => {
              // Find the image name first.
              const imageName = this.findImageName(this.images[index].src)
              //Dispatch to the store which handles the delete and the UI update
              this.$emit('deleteImage', {
                imageName,
                index,
              })
            },
          },
        ],
      })
    },
  },
  watch: {
    // When the route changes load the new images and reset the form
    $route(to, from) {
      // this.getImages(to.params.serialNumber)
      this.$refs.uploader.value = null
      this.selectedFile = null
      this.showUploadButton = false
    },
  },
}
</script>

<style lang='scss' scoped>
input {
  box-shadow: none;
}

input[type=submit] {
  background-color: pink;
}
.animation {
  -webkit-animation-duration: 2.5s;
  animation-duration: 2.5s;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.thumbs {
  height: auto;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  box-shadow: 0 6px 6px rgba(0, 0, 0, 0.19), 0 2px 2px rgba(0, 0, 0, 0.23);
}
.preview {
  color: black;
}
.grid {
  border-bottom: none !important;
}
.head {
  font-weight: bold;
  @include respond-to('sm') {
    font-weight: normal;
  }
}
.images {
  text-align: center;
  margin-top: 0px;
  font-weight: bold;
}
.title {
  text-align: left;
  font-size: large;
  margin-bottom: 20px;
}
#image-tools {
  display: flex;
  justify-content: space-between;
  padding: 8px 0px;
}
.delete {
  color: black;
}
.image-uploader {
  max-width: 100%;
  background-color: white;
}
.vue-lb-container {
  padding: 0px;
}
.upload-button {
  background-color: $ruxGreen;
  height: 40px;
}
</style>